import api from "./api";
import { UserInfo, clearSession, getOrgId, getTxnId } from "../common";

// challenge_name values returned by POST /v1/auth/login:
//   FACE_MFA              — password changed, face registered → /verify-mfa
//   FACE_REGISTRATION     — password changed, face not registered → /face-registration
//   FIRST_TIME_MFA        — password not changed, face not registered →
//                           /change-password → /face-registration → /verify-mfa
//   NEW_PASSWORD_REQUIRED — password not changed, face registered →
//                           /change-password → /verify-mfa
//   SECRET_QUESTION_REQUIRED — face registered but secret-question enrollment unfinished
//                           (dropped off mid-enrollment) → resume at /secret-questions
export type LoginChallenge =
  | "FACE_MFA" // face multi-factor authentication
  | "FACE_REGISTRATION"
  | "FIRST_TIME_MFA"
  | "NEW_PASSWORD_REQUIRED"
  | "SECRET_QUESTION_REQUIRED";

function randomId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

let flowId = "";

const CHALLENGE_KEY = "auth-challenge";

// The in-progress auth challenge persisted between the password step and completing the
// follow-up challenge (change-password / face-registration / verify-mfa).
interface AuthChallenge {
  flowId: string;
  username: string;
  userId: string;
  session: string;
  tempPassword: string;
  // Original challenge_name from login — used after change-password to pick the next route.
  challengeName: LoginChallenge | "NONE";
}

function saveChallenge(challenge: AuthChallenge): void {
  try {
    localStorage.setItem(CHALLENGE_KEY, JSON.stringify(challenge));
  } catch {
    /* ignore storage errors */
  }
}

function loadChallenge(): AuthChallenge | null {
  try {
    return JSON.parse(localStorage.getItem(CHALLENGE_KEY) ?? "null") as AuthChallenge | null;
  } catch {
    return null;
  }
}

export function getChallengeSession(): string {
  return loadChallenge()?.session ?? "";
}

export function getPendingChallengeName(): LoginChallenge | "NONE" {
  return loadChallenge()?.challengeName ?? "NONE";
}

export function setChallengeSession(session: string): void {
  const challenge = loadChallenge();
  if (challenge) saveChallenge({ ...challenge, session });
}

export function clearChallenge(): void {
  localStorage.removeItem(CHALLENGE_KEY);
}

// Exported so the secret-question service shares one X-Flow-ID with the rest of the login
// flow. flowId is a module-level value reset on refresh, so restore it from the persisted
// challenge when a caller in another module reaches this after a reload.
export function tracingHeaders(): Record<string, string> {
  if (!flowId) flowId = loadChallenge()?.flowId ?? "";
  return { "X-Request-ID": randomId(), "X-Flow-ID": flowId };
}

interface LoginResponseBody {
  status: string;
  challenge_name?: LoginChallenge;
  cognito_session_id?: string;
  session_id?: string;
  session?: string;
  user_id?: string;
  // Whether the user has already enrolled secret questions. Drives the enrollment gate.
  secret_questions_registered?: boolean;
}

// Read the challenge session regardless of which field name the backend used.
function extractSession(body: LoginResponseBody): string {
  return body.cognito_session_id ?? body.session_id ?? body.session ?? "";
}

// The user info that identifies the signed-in user throughout the app.
function buildUser(username: string, orgId: string): UserInfo {
  return { sub: username, username, orgId };
}

export interface LoginResult {
  challenge: LoginChallenge | "NONE";
  user: UserInfo;
  session: string;
  // From secret_questions_registered on the response (defaults false until the BE sends it).
  // Drives the secret-question enrollment gate via signin().
  secretRegistered: boolean;
}

// Step 1: verify User ID / password and start the MFA flow.
export async function login(username: string, password: string): Promise<LoginResult> {
  flowId = randomId(); // start a new flow on each password submit
  const orgId = getOrgId();
  const res = await api.post<LoginResponseBody>(
    "/v1/auth/login",
    { username, password, org_id: orgId },
    { headers: tracingHeaders() }
  );
  const body = res.data;

  const challenge: LoginChallenge | "NONE" =
    body.status === "challenge_required" && body.challenge_name ? body.challenge_name : "NONE";

  const session = extractSession(body);
  if (challenge !== "NONE" && !session) {
    return Promise.reject(
      new Error(`Login returned ${challenge} without a session (check the /v1/auth/login response)`)
    );
  }
  // The backend returns the canonical user_id at login; persist it so the change-password
  // step can send it. Fall back to the typed username if the response omits it.
  const userId = body.user_id ?? username;
  saveChallenge({
    flowId,
    username,
    userId,
    session,
    tempPassword: password,
    challengeName: challenge,
  });

  return {
    challenge,
    user: buildUser(username, orgId),
    session,
    secretRegistered: body.secret_questions_registered ?? false,
  };
}

// Complete FIRST_TIME_MFA / NEW_PASSWORD_REQUIRED by setting a new password.
export async function completeNewPassword(newPassword: string): Promise<LoginResult> {
  const challenge = loadChallenge();
  if (!challenge?.session) {
    return Promise.reject(new Error("No pending password-change challenge"));
  }
  flowId = challenge.flowId || flowId; // keep the same trace flow across a reload
  const orgId = getOrgId();
  const res = await api.post<LoginResponseBody>(
    "/v1/auth/change-password",
    {
      org_id: orgId,
      session_id: challenge.session,
      temporary_password: challenge.tempPassword,
      new_password: newPassword,
      user_id: challenge.userId,
      // Replay the business key typed at /login so OIDC preferred_username is
      // populated on the first-login path (BE has no reverse lookup from user_id).
      link_key: challenge.username,
    },
    { headers: tracingHeaders() }
  );
  const body = res.data;

  const nextChallenge: LoginChallenge | "NONE" =
    body.status === "challenge_required" && body.challenge_name ? body.challenge_name : "NONE";
  const session = extractSession(body) || challenge.session;
  saveChallenge({ ...challenge, session, tempPassword: "" });

  return {
    challenge: nextChallenge,
    user: buildUser(challenge.username, orgId),
    session,
    secretRegistered: body.secret_questions_registered ?? false,
  };
}

// The verify-mfa outcome:
//   200 { status: "authenticated", redirect_url }             → success, back to Salesforce
//   401 { error:{code:"near_miss"}, fallback_available:true }  → face was a close match;
//        surfaced here as status "fallback_available" so the page offers the secret question
//   any other rejection                                        → failed attempt (rethrown)
export interface VerifyMfaResult {
  status?: string;
  message?: string;
  redirectUrl?: string;
}

// A dead pending challenge (expired / invalid / already consumed session) is TERMINAL:
// re-capturing can only fail the same way, and the secret-question fallback runs on that
// same session_id, so it can't rescue it either — the user has to log in again. The BE has
// no dedicated error code for this yet, hence the code-then-message match.
export function isSessionError(err: any): boolean {
  const code = String(err?.error?.code ?? err?.code ?? "");
  if (/session|challenge|expired/i.test(code)) return true;
  const message = String(err?.error?.message ?? err?.message ?? "");
  return message.includes("セッション") || message.includes("FACE_MFA session");
}

export async function verifyMfa(image: Blob): Promise<VerifyMfaResult> {
  const challenge = loadChallenge();
  if (!challenge?.session) {
    return Promise.reject(new Error("No pending FACE_MFA session"));
  }
  flowId = challenge.flowId || flowId;
  const form = new FormData();
  form.append("files", image, "face.jpg");
  form.append("session_id", challenge.session);
  form.append("txn_id", getTxnId());
  try {
    const res = await api.post("/v1/auth/verify-mfa", form, { headers: tracingHeaders() });
    const body = (res.data ?? {}) as { status?: string; message?: string; redirect_url?: string };
    // Success → redirect back to Salesforce; drop the (now spent) session.
    if (body.status === "authenticated" || body.redirect_url) clearChallenge();
    return { status: body.status, message: body.message, redirectUrl: body.redirect_url };
  } catch (err: any) {
    // Near-miss: the BE rejects with 401 { error:{code:"near_miss"}, fallback_available:true }.
    // The face was close enough to offer the secret-question fallback — surface it as a typed
    // result (not an error) and KEEP the session so the fallback flow can reuse it.
    if (err?.fallback_available === true || err?.error?.code === "near_miss") {
      return { status: "fallback_available", message: err?.error?.message };
    }
    throw err;
  }
}

export function signOut(): void {
  flowId = "";
  clearChallenge();
  clearSession();
}
